/**
 * Fassungen eines Abschnitts: Liste (neueste zuerst), Vergleich einer Fassung mit dem aktuellen
 * Stand (Entwurf oder geltend), Wiederherstellen. Wiederherstellen macht die Fassung zum Entwurf und
 * legt sie als neue Fassung an — nichts geht verloren.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bookmark, Check, Pencil, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { useGuidelinesAPI, type SectionVersions } from "./GuidelinesAPI";
import { textDiff } from "./textDiff";

const when = (at: string) => new Date(at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

export function VersionsDialog(props: {
  customer: string;
  documentId: string;
  sectionId: string;
  heading: string;
  canRestore: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestored: () => void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [data, setData] = useState<SectionVersions | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = (select?: number) =>
    api.listVersions(props.documentId, props.sectionId).then((d) => {
      setData(d);
      if (select != null) setSelected(select);
    });

  /** Den Namen einer Fassung speichern (leer entfernt ihn). */
  const saveLabel = async (rev: number) => {
    setBusy(true);
    try {
      await api.labelVersion(props.documentId, props.sectionId, rev, editLabel.trim());
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Den heutigen Stand als benannte Fassung festhalten. */
  const keepSnapshot = async () => {
    if (!snapshot.trim()) return;
    setBusy(true);
    try {
      const r = await api.createVersion(props.documentId, props.sectionId, snapshot.trim());
      toast.success(t(r.created ? "guidelinesPage.versions.kept" : "guidelinesPage.versions.keptLabel", { rev: r.rev, label: r.label }));
      setSnapshot("");
      await reload(r.rev);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!props.open) return;
    setData(null);
    setSelected(null);
    setText(null);
    api
      .listVersions(props.documentId, props.sectionId)
      .then((d) => {
        setData(d);
        setSelected(d.items[0]?.rev ?? null);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.documentId, props.sectionId]);

  useEffect(() => {
    if (selected == null) return;
    setText(null);
    api.getVersion(props.documentId, props.sectionId, selected).then((v) => setText(v.text), () => setText(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Was sich von der gewählten Fassung bis heute geändert hat.
  const parts = useMemo(() => (text != null && data ? textDiff(text, data.current) : []), [text, data]);
  const changed = parts.some((p) => p.kind !== "same");

  const restore = async () => {
    if (selected == null) return;
    setRestoring(true);
    try {
      const r = await api.restoreVersion(props.documentId, props.sectionId, selected);
      toast.success(t("guidelinesPage.versions.restored", { rev: selected, newRev: r.rev }));
      props.onRestored();
      props.onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setRestoring(false);
    }
  };

  const originText = (v: SectionVersions["items"][number]) =>
    v.origin === "restore" ? t("guidelinesPage.versions.origin.restore", { rev: v.restoredFrom }) : t(`guidelinesPage.versions.origin.${v.origin}`);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle>{t("guidelinesPage.versions.title", { heading: props.heading })}</DialogTitle>
          <DialogDescription>{t("guidelinesPage.versions.intro")}</DialogDescription>
        </DialogHeader>
        {props.canRestore ? (
          <div className="flex items-center gap-2">
            <Input
              value={snapshot}
              onChange={(e) => setSnapshot(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && keepSnapshot()}
              placeholder={t("guidelinesPage.versions.snapshotPlaceholder")}
              aria-label={t("guidelinesPage.versions.snapshotPlaceholder")}
              maxLength={120}
              className="h-8"
            />
            <Button size="sm" variant="outline" onClick={keepSnapshot} disabled={busy || !snapshot.trim()}>
              <Bookmark size={14} className="mr-1" />
              {t("guidelinesPage.versions.snapshot")}
            </Button>
          </div>
        ) : null}
        {!data ? (
          <p className="text-sm text-muted-foreground">{t("guidelinesPage.versions.loading")}</p>
        ) : !data.items.length ? (
          <p className="text-sm text-muted-foreground">{t("guidelinesPage.versions.none")}</p>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-[16rem_1fr] gap-3">
            <ol className="min-h-0 overflow-y-auto rounded-md border" data-testid="version-list">
              {data.items.map((v, i) => (
                <li key={v.rev} className="group relative">
                  {editing === v.rev ? (
                    <div className="flex items-center gap-1 border-b bg-accent px-2 py-2">
                      <Input
                        autoFocus
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveLabel(v.rev);
                          if (e.key === "Escape") setEditing(null);
                        }}
                        placeholder={t("guidelinesPage.versions.labelPlaceholder")}
                        aria-label={t("guidelinesPage.versions.labelPlaceholder")}
                        maxLength={120}
                        className="h-7 text-sm"
                      />
                      <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" aria-label={t("guidelinesPage.versions.saveLabel")} onClick={() => saveLabel(v.rev)} disabled={busy}>
                        <Check size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" aria-label={t("guidelinesPage.cancel")} onClick={() => setEditing(null)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                  <button
                    type="button"
                    onClick={() => setSelected(v.rev)}
                    className={cn("w-full border-b px-3 py-2 text-left text-sm hover:bg-accent", v.rev === selected && "bg-accent")}
                    data-version={v.rev}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium tabular-nums">{t("guidelinesPage.versions.rev", { rev: v.rev })}</span>
                      {i === 0 ? <span className="rounded bg-primary/10 px-1 text-[10px] text-primary">{t("guidelinesPage.versions.latest")}</span> : null}
                      <span className="ml-auto text-xs text-muted-foreground">{when(v.at)}</span>
                    </div>
                    {v.label ? <div className="truncate text-sm font-medium" data-version-label>{v.label}</div> : null}
                    <div className="text-xs text-muted-foreground">{originText(v)}</div>
                    {v.authors.length ? <div className="truncate text-xs text-muted-foreground">{v.authors.join(", ")}</div> : null}
                  </button>
                  )}
                  {props.canRestore && editing !== v.rev ? (
                    <button
                      type="button"
                      className="absolute bottom-1 right-1 rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-foreground focus:opacity-100 group-hover:opacity-100"
                      title={t("guidelinesPage.versions.rename")}
                      aria-label={t("guidelinesPage.versions.rename")}
                      onClick={() => {
                        setEditing(v.rev);
                        setEditLabel(v.label ?? "");
                      }}
                    >
                      <Pencil size={13} />
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
            <div className="flex min-h-0 flex-col gap-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t(data.hasDraft ? "guidelinesPage.versions.compareDraft" : "guidelinesPage.versions.compareCurrent", { rev: selected })}</span>
                {props.canRestore && selected != null ? (
                  <Button size="sm" variant="outline" className="ml-auto" onClick={restore} disabled={restoring || !changed}>
                    <RotateCcw size={14} className="mr-1" />
                    {t("guidelinesPage.versions.restore", { rev: selected })}
                  </Button>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap rounded-md border bg-background p-3 text-sm leading-6" data-testid="version-diff">
                {text == null ? (
                  <span className="text-muted-foreground">{t("guidelinesPage.versions.loading")}</span>
                ) : !changed ? (
                  <span className="text-muted-foreground">{t("guidelinesPage.versions.same")}</span>
                ) : (
                  parts.map((p, i) => (
                    <span
                      key={i}
                      className={cn(p.kind === "added" && "bg-emerald-100 dark:bg-emerald-900/40", p.kind === "removed" && "bg-rose-100 text-muted-foreground line-through dark:bg-rose-900/40")}
                    >
                      {p.text}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
